import {Component, OnInit} from '@angular/core';
import {ProductCard} from "../models/productCard";
import {ShoppingCartService} from "../service/shoppingCart.service";
import {ShoppingCartResponse} from "../models/shoppingCartResponse";
import {ImageService} from "../service/image.service";
import {CurrencyService} from "../service/currency.service";
import {ShoppingCartRequest} from "../models/shoppingCartRequest";
import {Subscription} from "rxjs";
import {LanguageService} from "../service/language.service";
import {UserService} from "../service/user.service";
import {Router} from "@angular/router";
import {SharedConstants} from "../utilityClass/sharedConstants";
import {SharedService} from "../service/shered.service";
import {ShoppingSharedService} from "../service/shopping-shared.service";
import {CurrencyResponse} from "../interface/currencyResponse";

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss', './cart-media.component.scss']
})
export class CartComponent implements OnInit {
  // todo: delete this and add the real data when ready
  protected productCards = [
    new ProductCard(1),
    new ProductCard(2),
    new ProductCard(3),
    new ProductCard(4)
  ];

  currencyRate!: number
  shoppingCart!: ShoppingCartResponse
  shoppingCartRequest!: ShoppingCartRequest
  currentLanguage!: string;
  loading: boolean = true
  private languageSubscription: Subscription | undefined;

  constructor(private shoppingCartService: ShoppingCartService,
              private imageService: ImageService,
              private currencyService: CurrencyService,
              private languageService: LanguageService,
              private userService: UserService,
              private router: Router,
              private sharedService: SharedService,
              private shoppingSharedService: ShoppingSharedService
  ) {
  }

  ngOnInit(): void {
    this.languageSubscription = this.languageService.currentLanguage$.subscribe((lang) => {
      this.currentLanguage = lang;
      this.loadCart();
    });
  }

  loadCart(): void {
    this.shoppingCartService.getBucketForCurrentUser(this.currentLanguage).subscribe(data => {
      this.shoppingCart = data;
      this.convertPrices();
      this.loading = false;
    });
  }

  convertPrices(): void {
    if (!this.shoppingCart?.items?.length) {
      return;
    }

    this.currencyService.getCurrency(SharedConstants.USD).subscribe({
      next: (data: CurrencyResponse) => {
        const currencyRate = Number(data?.[SharedConstants.USD]);
        this.setCurrencyRate(currencyRate);
        this.applyConvertedPrices();
      },
      error: () => {
        this.setCurrencyRate(SharedConstants.DOLLAR_EXCHANGE_RATE);
        this.applyConvertedPrices();
      }
    });
  }

  private setCurrencyRate(rate: number): void {
    this.currencyRate = Number.isFinite(rate) && rate > 0
      ? rate
      : SharedConstants.DOLLAR_EXCHANGE_RATE;
  }

  private applyConvertedPrices(): void {
    this.shoppingCart?.items.forEach(item => {
      item.convertedPrice = item.product.price / this.currencyRate;
    });
  }

  increaseProductQuantity(cartItem: any): void {
    cartItem.quantity++;
    this.updateTotalAmount();
    this.shoppingCartRequest = {
      productId: cartItem.product.id,
      quantity: cartItem.quantity,
      attributeValueIds: [cartItem.attributeValues[0].id],
      isFromOutOfCart: false
    };
    this.shoppingCartService.addProductToBucket(this.shoppingCartRequest).subscribe({
      next: () => {
        this.sharedService.needToUpdate();
      },
      error: (err) => {
        console.error('Failed to add to cart', err);
      },
    });
  }

  reduceProductQuantity(cartItem: any): void {
    if (cartItem.quantity > 1) {
      cartItem.quantity--;
      this.updateTotalAmount();
      this.shoppingCartRequest = {
        productId: cartItem.product.id,
        quantity: cartItem.quantity,
        attributeValueIds: [cartItem.attributeValues[0].id],
        isFromOutOfCart: false
      };
      this.shoppingCartService.addProductToBucket(this.shoppingCartRequest).subscribe({
        next: () => {
          this.sharedService.needToUpdate();
        },
        error: (err) => console.error('Failed to add to cart', err),
      });
    }
  }

  updateTotalAmount(): void {
    if (this.shoppingCart) {
      this.shoppingCart.totalAmount = this.shoppingCart.items.reduce(
        (total, item) => total + item.product.price * item.quantity,
        0
      );
    }
  }

  getTotalPriceUSD(cartItem: any): number {
    return cartItem.convertedPrice * cartItem.quantity;
  }

  getProductImageUrl(imageName: string): string {
    return this.imageService.getImageUrl(imageName);
  }

  onRemoveItem(cartItemId: number): void {
    this.shoppingCartService.removeProductFromBucket(cartItemId).subscribe({
      next: () => {
        this.loadCart()
        this.sharedService.needToUpdate()
      },
      error: (err) => {
        console.error('Failed to remove item', err);
      }
    });
  }

  checkout() {
    if (!this.userService.isLoggedIn()) {
      this.userService.setRedirectUrl(this.router.url);
      this.router.navigate(['/login']);
      return;
    }

    if (!this.shoppingCart?.items?.length) {
      return;
    }

    const shopData = this.shoppingCart.items.map((item: any) => ({
      product: item.product,
      quantity: item.quantity,
      selectedColor: item.attributeValues?.[0]?.value ?? null,
      priceAMD: item.product.price * item.quantity,
      priceUSD: (item.convertedPrice ?? 0) * item.quantity,
      shoppingCartId: this.shoppingCart.id
    }));

    const totalSumAmd = shopData.reduce((sum, curr) => sum + curr.priceAMD, 0);
    const totalSumUsd = shopData.reduce((sum, curr) => sum + curr.priceUSD, 0);

    this.shoppingSharedService.products = shopData;
    this.shoppingSharedService.totalSumAmd = totalSumAmd;
    this.shoppingSharedService.totalSumUsd = totalSumUsd;

    this.router.navigate(['/shipping-method'], {
      state: { shopData, totalSumAmd, totalSumUsd }
    });
  }
}
